import { useEffect } from "react";
import {
    Phone,
    ArrowUpRight,
    Check,
    Flame,
    Tv,
    Layers,
    Zap,
    ShieldCheck,
    Award,
    Clock,
    Wrench,
    Ruler,
    Home as HomeIcon,
    Square,
    Sparkles,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Header, AnnouncementBar, Footer } from "@/components/Layout";
import { SITE } from "@/lib/site";
import { useSeo, breadcrumbSchema, serviceSchema } from "@/lib/seo";

const IMG_DRYWALL = "/images/media-walls/drywall-six-niches.webp";
const IMG_WOOD = "/images/media-walls/wood-fireplace.jpg";
const IMG_MODERN = "/images/media-walls/shiplap-dark-angle.png";

const STYLES = [
    {
        i: Square,
        badge: "Warm · natural · statement",
        title: "Wood Media Walls",
        blurb: "Solid wood, engineered wood plank, or slat-wood accent walls with a recessed electric fireplace and mounted TV. Warm, natural, and dead-simple to keep looking new.",
        bullets: [
            "Solid oak, walnut, cedar, or maple accent panels",
            "Vertical slat-wood detail for a modern look",
            "Engineered-wood plank for wide, uniform coverage",
            "Optional floating shelves in matching wood",
            "Recessed electric fireplace niche cut clean",
            "Cable pass-throughs — no visible wires",
        ],
        img: IMG_WOOD,
    },
    {
        i: Layers,
        badge: "Coastal · farmhouse · trending",
        title: "Shiplap Media Walls",
        blurb: "Painted or natural shiplap board wraps the whole wall with a clean, tongue-and-groove pattern. Frames the fireplace and TV beautifully — a favorite for farmhouse, coastal, and PNW-modern homes.",
        bullets: [
            "Real cedar or pine shiplap (not peel-and-stick)",
            "Painted white / off-white or stained natural",
            "Tight, uniform reveals — no messy gaps",
            "Fireplace and TV framed flush into the wall",
            "Optional built-in shelving beside the fireplace",
            "Trim-work finish: crown, base, and corners",
        ],
        img: IMG_MODERN,
    },
    {
        i: Sparkles,
        badge: "Clean · modern · custom",
        title: "Drywall Media Walls",
        blurb: "A full-height drywall build-out with recessed niches for the TV, fireplace, and shelving. Textured, painted, and finished to match your walls — the ultimate seamless, custom look.",
        bullets: [
            "Full-height built-out wall (floor-to-ceiling optional)",
            "Recessed TV niche — flush-mount, no bezel gap",
            "Fireplace opening framed and firebox-ready",
            "Integrated LED cove lighting",
            "Texture and paint matched to existing walls",
            "Optional stone / tile accent around the firebox",
        ],
        img: IMG_DRYWALL,
    },
];

const FIREPLACE_FEATURES = [
    { i: Flame, t: "Realistic Flame Effects", d: "Modern LED flame technology with adjustable brightness, color, and pattern. Looks like a real fire — no gas line, no chimney." },
    { i: Zap, t: "Zero Combustion · Zero Vent", d: "No gas, no wood, no venting required. Plug into a standard outlet or wire it in for a cleaner install." },
    { i: HomeIcon, t: "Adjustable Heat Output", d: "Most units run 400–1,500W of supplemental heat — enough to take the chill off a living room. Flame effect works with the heater off in summer." },
    { i: ShieldCheck, t: "Kid & Pet Safe", d: "The glass stays cool. No open flame, no burn risk, no carbon monoxide." },
];

const WHY_US = [
    { i: Award, t: "Full-Service GC", d: "Framing, electrical, drywall, finish work, paint — one contractor, one crew." },
    { i: ShieldCheck, t: "Licensed & Insured", d: "Oregon CCB #232477. Every wire, every bracket, every nail — done right." },
    { i: Clock, t: "Fast Turnaround", d: "Most media walls install in 3–5 days depending on style and complexity." },
    { i: Wrench, t: "Clean, Tidy Job Site", d: "Plastic, drop cloths, and dust containment. Your living room stays livable." },
];

const FAQ = [
    { q: "How long does a media wall installation take?", a: "Simple shiplap or wood media walls typically install in 2–3 days. Full drywall build-outs with recessed niches, integrated lighting, and paint typically run 4–7 days depending on complexity." },
    { q: "Do I need a gas line for the fireplace?", a: "No — we install electric fireplaces exclusively. They plug into a standard 120V outlet or hardwire directly. No gas line, no venting, no chimney. Cleaner install, safer, cheaper to run." },
    { q: "How much heat does an electric fireplace put out?", a: "Most residential electric fireplaces produce 400–1,500 watts of supplemental heat — enough to warm a 400–600 sq ft room. The flame effect also runs independently, so you can enjoy the ambience without the heat in summer." },
    { q: "Can you match the media wall to my existing living room paint or trim?", a: "Yes. We can match wall paint, trim color, and even blend the media wall drywall texture into your existing walls so it looks original — not like an add-on." },
    { q: "Can you build a media wall in a manufactured home?", a: "Yes — we do a lot of manufactured-home living rooms. Wall depth and stud spacing are a little different than site-built homes, but we know how to work with them. Shiplap and wood-plank styles are especially popular for these installs." },
];

export default function MediaWalls() {
    useSeo({
        title: "Media Wall Installation with Electric Fireplaces · Medford, Oregon | 5 Star Construction",
        description: "Custom media wall installation in Medford, Ashland, and the Rogue Valley — wood, shiplap, and drywall media walls with built-in electric fireplaces and mounted TVs. Licensed Oregon GC (CCB #232477). Free in-home quotes — 541-292-9620.",
        path: "/media-walls",
        jsonLdKey: "media-walls",
        jsonLd: [
            serviceSchema({
                name: "Media Wall Installation with Electric Fireplaces",
                description: "Custom-built media walls in wood, shiplap, and drywall styles — all with integrated electric fireplaces and TV mounting. Framing, electrical, drywall, and finish work by one contractor in Medford and Southern Oregon.",
                path: "/media-walls",
                serviceType: "Media Wall & Fireplace Installer",
            }),
            breadcrumbSchema([
                { name: "Home", path: "/" },
                { name: "Services", path: "/#services" },
                { name: "Media Walls", path: "/media-walls" },
            ]),
            {
                "@context": "https://schema.org",
                "@type": "FAQPage",
                mainEntity: FAQ.map((it) => ({
                    "@type": "Question",
                    name: it.q,
                    acceptedAnswer: { "@type": "Answer", text: it.a },
                })),
            },
        ],
    });
    useEffect(() => {}, []);

    return (
        <div className="bg-[#F6F5F2] min-h-screen">
            <AnnouncementBar />
            <Header />

            {/* Hero — split showing both sheetrock + shiplap styles */}
            <section className="relative bg-[#111111] text-[#F6F5F2] border-b-4 border-[#E11D2A] overflow-hidden">
                <div className="absolute inset-0 opacity-[0.07] pointer-events-none" style={{ backgroundImage: "radial-gradient(circle at 1px 1px, #FFD400 1px, transparent 0)", backgroundSize: "16px 16px" }} />
                <div className="relative max-w-[1400px] mx-auto px-6 md:px-12 lg:px-20 py-20 md:py-28 grid lg:grid-cols-12 gap-10 lg:gap-14 items-center">
                    <div className="lg:col-span-6">
                        <div className="inline-flex items-center gap-2 mb-6 bg-[#FFD400] border border-[#FFD400] px-3 py-1.5">
                            <Tv className="w-4 h-4 text-[#111111]" strokeWidth={2.5} />
                            <span className="font-['Manrope'] text-xs font-black tracking-[0.22em] uppercase text-[#111111]">Custom Builds · Medford, OR</span>
                        </div>
                        <h1 className="font-['Anton'] tracking-tight leading-[0.92] text-5xl sm:text-6xl lg:text-7xl uppercase">
                            Custom builds.
                            <br />
                            <span className="text-[#FFD400]">Real sheetrock.</span>
                            <br />
                            <span className="text-[#F6F5F2]">Real shiplap.</span>
                        </h1>
                        <p className="mt-7 font-['Manrope'] text-lg md:text-xl leading-relaxed text-[#F6F5F2]/80 max-w-2xl">
                            Not peel-and-stick. Not a kit. Every media wall we build is framed on-site, wired for a recessed electric fireplace, and finished in <strong className="text-[#F6F5F2]">real sheetrock compartments</strong> or <strong className="text-[#F6F5F2]">real shiplap board</strong> — designed around your TV, your room, your style. One licensed Oregon crew from framing to final coat.
                        </p>
                        <div className="mt-9 flex flex-col sm:flex-row gap-4">
                            <a href={SITE.phoneHref} data-testid="mw-call-cta">
                                <Button className="rounded-none h-14 px-7 bg-[#E11D2A] hover:bg-[#B0121E] text-white font-bold tracking-wide text-base font-['Manrope']">
                                    <Phone className="w-5 h-5 mr-3" strokeWidth={2.5} />
                                    CALL {SITE.phoneDisplay}
                                </Button>
                            </a>
                            <a href="/#contact" data-testid="mw-quote-cta">
                                <Button variant="outline" className="rounded-none h-14 px-7 border-2 border-[#F6F5F2] bg-transparent text-[#F6F5F2] hover:bg-[#F6F5F2] hover:text-[#111111] font-bold tracking-wide text-base font-['Manrope']">
                                    FREE MEDIA WALL QUOTE
                                    <ArrowUpRight className="w-5 h-5 ml-2" strokeWidth={2.5} />
                                </Button>
                            </a>
                        </div>
                    </div>
                    {/* Split-hero: two real reference photos side by side */}
                    <div className="lg:col-span-6 relative">
                        <div className="grid grid-cols-2 gap-2 md:gap-3">
                            <div className="relative aspect-[3/4] overflow-hidden border-2 border-[#F6F5F2]/20 shadow-block bg-[#111111]" data-testid="mw-hero-sheetrock">
                                <img src="/images/media-walls/drywall-compartments-lit.jpg" alt="Custom sheetrock media wall with lit vertical compartments framing a recessed electric fireplace and mounted TV — Southern Oregon build" loading="eager" className="absolute inset-0 w-full h-full object-cover" />
                                <div className="absolute bottom-0 inset-x-0 bg-[#FFD400] text-[#111111] px-3 py-2">
                                    <div className="font-['Manrope'] text-[10px] uppercase tracking-[0.2em] font-black leading-none">Style 01</div>
                                    <div className="font-display text-sm md:text-base font-extrabold mt-1 leading-tight">Sheetrock compartments</div>
                                </div>
                            </div>
                            <div className="relative aspect-[3/4] overflow-hidden border-2 border-[#F6F5F2]/20 shadow-block bg-[#111111]" data-testid="mw-hero-shiplap">
                                <img src="/images/media-walls/shiplap-dark-fireplace.png" alt="Dark stained shiplap media wall with electric fireplace and mounted TV — custom-built accent wall" loading="eager" className="absolute inset-0 w-full h-full object-cover" />
                                <div className="absolute bottom-0 inset-x-0 bg-[#F6F5F2] text-[#111111] px-3 py-2">
                                    <div className="font-['Manrope'] text-[10px] uppercase tracking-[0.2em] font-black leading-none">Style 02</div>
                                    <div className="font-display text-sm md:text-base font-extrabold mt-1 leading-tight">Real shiplap board</div>
                                </div>
                            </div>
                        </div>
                        <div className="mt-3 md:mt-4 bg-[#111111] border-2 border-[#F6F5F2]/20 px-4 py-3 flex items-center justify-between gap-3">
                            <div>
                                <div className="font-['Manrope'] text-[10px] uppercase tracking-[0.22em] font-bold text-[#FFD400]">Every build includes</div>
                                <div className="font-display text-sm md:text-base font-extrabold text-[#F6F5F2] leading-tight">Recessed electric fireplace · No gas · No chimney</div>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* Intro / why us */}
            <section className="bg-white border-b border-[#E2DFD8]">
                <div className="max-w-[1400px] mx-auto px-6 md:px-12 lg:px-20 py-20 md:py-28">
                    <div className="grid lg:grid-cols-12 gap-8 mb-12">
                        <div className="lg:col-span-5">
                            <div className="font-['Manrope'] text-sm font-bold tracking-[0.22em] uppercase text-[#E11D2A]">Why homeowners pick us</div>
                            <h2 className="mt-4 font-display font-extrabold tracking-tight text-[#111812] text-4xl sm:text-5xl">
                                One crew.
                                <br />
                                One clean install.
                            </h2>
                        </div>
                        <div className="lg:col-span-7 lg:pt-3">
                            <p className="font-['Manrope'] text-base md:text-lg text-[#5F5F5F] leading-relaxed">
                                A media wall isn&apos;t just a decorative face — it&apos;s framing, electrical, drywall, finish carpentry, and paint, all working together. We handle every piece under one contractor, one schedule, one line of accountability. Your living room stays livable while we build.
                            </p>
                        </div>
                    </div>
                    <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
                        {WHY_US.map(({ i: I, t, d }) => (
                            <div key={t} className="bg-[#F6F5F2] border border-[#E2DFD8] hover:border-[#111111] hover:shadow-block-sm transition-all p-6">
                                <div className="w-12 h-12 bg-[#E11D2A] text-white border border-[#111111] flex items-center justify-center mb-5">
                                    <I className="w-5 h-5" strokeWidth={2.25} />
                                </div>
                                <div className="font-display text-lg font-extrabold text-[#111812]">{t}</div>
                                <p className="mt-2 font-['Manrope'] text-sm text-[#5F5F5F] leading-relaxed">{d}</p>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* Three styles */}
            {STYLES.map(({ i: I, badge, title, blurb, bullets, img }, idx) => {
                const bg = idx % 2 === 0 ? "bg-[#F6F5F2]" : "bg-white";
                const reverse = idx % 2 === 1;
                return (
                    <section key={title} className={`${bg} border-b border-[#E2DFD8]`}>
                        <div className="max-w-[1400px] mx-auto px-6 md:px-12 lg:px-20 py-20 md:py-28 grid lg:grid-cols-12 gap-12 items-center">
                            <div className={`lg:col-span-6 ${reverse ? "lg:order-2" : ""}`}>
                                <div className="aspect-[5/4] overflow-hidden border-2 border-[#111111] shadow-block bg-[#111111] max-w-lg lg:max-w-none mx-auto">
                                    <img src={img} alt={`${title} — example installation`} loading="lazy" className="w-full h-full object-cover" />
                                </div>
                            </div>
                            <div className={`lg:col-span-6 ${reverse ? "lg:order-1" : ""}`}>
                                <div className="inline-flex items-center gap-2 bg-[#111111] text-[#FFD400] px-3 py-1.5">
                                    <I className="w-4 h-4" strokeWidth={2.5} />
                                    <span className="font-['Manrope'] text-xs font-black tracking-[0.22em] uppercase">0{idx + 1} · {badge}</span>
                                </div>
                                <h2 className="mt-4 font-['Anton'] uppercase tracking-tight leading-[0.95] text-[#111812] text-4xl sm:text-5xl">
                                    {title}
                                </h2>
                                <p className="mt-5 font-['Manrope'] text-base md:text-lg text-[#5F5F5F] leading-relaxed">
                                    {blurb}
                                </p>
                                <ul className="mt-7 grid sm:grid-cols-2 gap-3">
                                    {bullets.map((b) => (
                                        <li key={b} className="flex items-start gap-2.5 font-['Manrope'] text-sm text-[#111812]">
                                            <Check className="w-4 h-4 text-[#E11D2A] shrink-0 mt-1" strokeWidth={3} />
                                            <span>{b}</span>
                                        </li>
                                    ))}
                                </ul>
                            </div>
                        </div>
                    </section>
                );
            })}

            {/* Electric fireplace features */}
            <section className="bg-[#111111] text-[#F6F5F2] border-b-4 border-[#E11D2A]">
                <div className="max-w-[1400px] mx-auto px-6 md:px-12 lg:px-20 py-20 md:py-28">
                    <div className="text-center mb-12 max-w-3xl mx-auto">
                        <div className="font-['Manrope'] text-sm font-bold tracking-[0.22em] uppercase text-[#FFD400]">Every media wall gets one</div>
                        <h2 className="mt-3 font-['Anton'] uppercase tracking-tight leading-[0.95] text-4xl sm:text-5xl md:text-6xl">
                            Why electric fireplaces?
                        </h2>
                        <p className="mt-5 font-['Manrope'] text-base md:text-lg text-[#F6F5F2]/75">
                            Cleaner install, safer for kids and pets, cheaper to run than gas, and the flame effect looks incredible.
                        </p>
                    </div>
                    <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-px bg-[#F6F5F2]/15 border border-[#F6F5F2]/15">
                        {FIREPLACE_FEATURES.map(({ i: I, t, d }) => (
                            <div key={t} className="bg-[#111111] p-7" data-testid={`mw-feature-${t.toLowerCase().replace(/[^a-z]+/g, "-")}`}>
                                <div className="w-12 h-12 bg-[#FFD400] text-[#111111] border border-[#111111] flex items-center justify-center mb-5">
                                    <I className="w-5 h-5" strokeWidth={2.25} />
                                </div>
                                <div className="font-display text-lg font-extrabold text-[#F6F5F2]">{t}</div>
                                <p className="mt-2 font-['Manrope'] text-sm text-[#F6F5F2]/70 leading-relaxed">{d}</p>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* Process */}
            <section className="bg-white border-b border-[#E2DFD8]">
                <div className="max-w-[1400px] mx-auto px-6 md:px-12 lg:px-20 py-20 md:py-28">
                    <div className="text-center mb-14 max-w-3xl mx-auto">
                        <div className="font-['Manrope'] text-sm font-bold tracking-[0.22em] uppercase text-[#E11D2A]">How it works</div>
                        <h2 className="mt-3 font-['Anton'] uppercase tracking-tight leading-[0.95] text-[#111812] text-4xl sm:text-5xl">
                            Design, frame, wire, finish.
                        </h2>
                    </div>
                    <div className="grid md:grid-cols-4 gap-4">
                        {[
                            { i: Ruler, t: "Design", d: "In-home consult. Wall dimensions, TV size, fireplace choice, style selection." },
                            { i: HomeIcon, t: "Frame", d: "Build out the wall, cut the fireplace and TV niches, add blocking for shelves." },
                            { i: Zap, t: "Wire", d: "Outlets behind the TV, hardwire the firebox, cable pass-throughs — code-compliant." },
                            { i: Sparkles, t: "Finish", d: "Drywall/shiplap/wood applied, painted, trimmed, TV mounted, fireplace tested." },
                        ].map(({ i: I, t, d }, idx) => (
                            <div key={t} className="bg-[#F6F5F2] border border-[#E2DFD8] p-6 relative" data-testid={`mw-step-${idx}`}>
                                <div className="absolute -top-3 left-6 bg-[#111111] text-[#FFD400] font-['Anton'] text-lg px-3 py-1">
                                    0{idx + 1}
                                </div>
                                <div className="w-12 h-12 bg-[#E11D2A] text-white border border-[#111111] flex items-center justify-center mt-4 mb-4">
                                    <I className="w-5 h-5" strokeWidth={2.25} />
                                </div>
                                <div className="font-display text-lg font-extrabold text-[#111812]">{t}</div>
                                <p className="mt-2 font-['Manrope'] text-sm text-[#5F5F5F] leading-relaxed">{d}</p>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* FAQ */}
            <section className="bg-[#F6F5F2] border-b border-[#E2DFD8]">
                <div className="max-w-[1200px] mx-auto px-6 md:px-12 lg:px-20 py-20 md:py-28">
                    <div className="mb-12 max-w-3xl">
                        <div className="font-['Manrope'] text-sm font-bold tracking-[0.22em] uppercase text-[#E11D2A]">Frequently asked</div>
                        <h2 className="mt-3 font-['Anton'] uppercase tracking-tight leading-[0.95] text-[#111812] text-4xl sm:text-5xl">
                            Media wall questions we hear.
                        </h2>
                    </div>
                    <div className="space-y-4">
                        {FAQ.map((it, i) => (
                            <details key={it.q} className="group bg-white border border-[#E2DFD8] hover:border-[#111111] transition-colors" data-testid={`mw-faq-${i}`}>
                                <summary className="cursor-pointer list-none p-5 md:p-6 flex items-start justify-between gap-4 font-display text-base md:text-lg font-extrabold text-[#111812]">
                                    <span>{it.q}</span>
                                    <span className="text-[#E11D2A] font-['Anton'] text-2xl leading-none shrink-0 group-open:rotate-45 transition-transform">+</span>
                                </summary>
                                <div className="px-5 pb-5 md:px-6 md:pb-6 font-['Manrope'] text-base text-[#5F5F5F] leading-relaxed">{it.a}</div>
                            </details>
                        ))}
                    </div>
                </div>
            </section>

            {/* CTA */}
            <section className="bg-[#FFD400] border-b-4 border-[#111111]">
                <div className="max-w-[1400px] mx-auto px-6 md:px-12 lg:px-20 py-14 md:py-16 grid lg:grid-cols-12 gap-8 items-center">
                    <div className="lg:col-span-7">
                        <h2 className="font-['Anton'] uppercase tracking-tight leading-[0.95] text-[#111111] text-4xl sm:text-5xl">
                            Turn that blank wall into a <span className="bg-[#E11D2A] text-[#F6F5F2] px-3 py-0.5">focal point.</span>
                        </h2>
                        <p className="mt-4 font-['Manrope'] text-base md:text-lg text-[#111111]/85 max-w-2xl">
                            Free in-home consult. Real prices. Real timelines. CCB #232477.
                        </p>
                    </div>
                    <div className="lg:col-span-5 flex flex-col sm:flex-row gap-3">
                        <a href={SITE.phoneHref} className="block flex-1" data-testid="mw-cta-call">
                            <Button className="rounded-none w-full h-14 px-6 bg-[#111111] hover:bg-[#000000] text-white font-bold tracking-wide text-base font-['Manrope']">
                                <Phone className="w-5 h-5 mr-2" strokeWidth={2.5} />
                                CALL {SITE.phoneDisplay}
                            </Button>
                        </a>
                        <a href="/#contact" className="block flex-1" data-testid="mw-cta-form">
                            <Button variant="outline" className="rounded-none w-full h-14 px-6 border-2 border-[#111111] bg-transparent text-[#111111] hover:bg-[#111111] hover:text-white font-bold tracking-wide text-base font-['Manrope']">
                                REQUEST A QUOTE
                                <ArrowUpRight className="w-5 h-5 ml-2" strokeWidth={2.5} />
                            </Button>
                        </a>
                    </div>
                </div>
            </section>

            {/* Attribution */}
            <section className="bg-[#111111] text-[#F6F5F2]/55">
                <div className="max-w-[1400px] mx-auto px-6 md:px-12 lg:px-20 py-5 text-[11px] font-['Manrope'] leading-relaxed">
                    Example media wall &amp; electric fireplace photos via Pexels (free use). Used for illustration; not all are photos of jobs we&apos;ve personally completed.
                </div>
            </section>

            <Footer />
        </div>
    );
}
